import React, { ComponentType, useEffect, useRef } from 'react'
import { getAppContext } from 'src/context/AppContext'
import { useStore } from '@core'
import { useTran } from 'src/i18n/useTran'
import { Language } from 'src/api/defs/enums.type'
import { CountrySelector } from 'src/ui/country-selector/CountrySelector'
import { msg } from 'src/i18n/texts/keys'
import { locales } from 'src/i18n/locale'
import { filter, map, distinctUntilChanged } from 'rxjs/operators'
import { ClientUser } from 'src/api/defs/ClientUser.type'
import { useUser } from 'src/auth/user/useUser'
import { apiEnums } from 'src/api/defs/enums.model'
import { SetLocaleCmd } from 'src/api/defs/SetLocaleCmd.type'

type LanguageSwitcherProps = { onValueChanged?: () => void }

let lastRequestedLanguage: Language | null = null

export const LanguageSwitcher: ComponentType<LanguageSwitcherProps> = ({
    onValueChanged
}) => {
    const t = useTran()
    const [lang, setLang] = useStore(getAppContext().i18n.language)
    const user = useUser()
    const setLanguage = getAppContext().myZoneApi.setLanguage
    const languages = { ...apiEnums.Language }
    languages.of = locales

    const sub = getAppContext()
        .user.store.changes()
        .pipe(
            filter((user: ClientUser | null) => !!user?.userLanguage),
            map(u => u!.userLanguage!.toUpperCase() as Language),
            distinctUntilChanged()
        )
        .subscribe(setLang)

    useEffect(() => {
        return () => sub.unsubscribe()
    }, [])

    const lastSentRef = useRef<Language | null>(null)
    useEffect(() => {
        if (!user || !lang) return
        if (lastSentRef.current === lang) return
        if (lastRequestedLanguage === lang) return
        lastSentRef.current = lang as Language
        lastRequestedLanguage = lang as Language
        setLanguage({ userLanguage: lang as Language } as SetLocaleCmd).catch(
            error => {
                console.error('Failed to save user language', error)
                if (lastRequestedLanguage === lang) {
                    lastRequestedLanguage = null
                }
            }
        )
    }, [user, lang, setLanguage])

    useEffect(() => {
        const isLocaleSet = JSON.parse(
            window.localStorage.getItem('user.lang') || 'null'
        )
        if (!isLocaleSet && lang) {
            localStorage.setItem('user.lang', JSON.stringify(lang))
        }
    }, [lang])

    const customLabels = {
        SK: { primary: t(msg.enum.Language.SK) },
        CS: { primary: t(msg.enum.Language.CS) },
        EN: { primary: t(msg.enum.Language.EN) },
        UK: { primary: t(msg.enum.Language.UK) },
        PL: { primary: t(msg.enum.Language.PL) },
        HU: { primary: t(msg.enum.Language.HU) },
        RO: { primary: t(msg.enum.Language.RO) },
        IT: { primary: t(msg.enum.Language.IT) },
        DE: { primary: t(msg.enum.Language.DE) }
    }

    return (
        <CountrySelector
            selected={lang || Language.EN}
            onSelect={l => {
                setLang(l! as Language)
                onValueChanged?.()
            }}
            showSelectedLabel={false}
            showOptionLabel={true}
            customLabels={customLabels}
            countries={languages.of}
        />
    )
}
